import { action, computed, makeObservable, observable } from 'mobx'
import WorkspaceApplicant, {
  WorkspaceApplicantStatus,
} from 'admin/staffing/WorkspaceApplicant'
import WorkspacePosting from 'admin/staffing/WorkspacePosting'
import WorkspacePostingStore from 'admin/WorkspacePostingStore'
import { ApplicantManagementTableColumns } from '../ApplicantManagementTable/ApplicantManagementTableColumns'

/**
 * Checkbox selection states.
 */
export const enum SelectionState {
  NoneSelected = 'NoneSelected',
  SomeSelected = 'SomeSelected',
  AllSelected = 'AllSelected',
}

export default class ApplicantManagementPanelViewModel {
  /**
   * Whether the applicant management table panel is showing.
   */
  showing = false

  /**
   * Columns to populate the applicant management table header and rows with.
   */
  columns = ApplicantManagementTableColumns

  /**
   * Search text.
   */
  searchText = ''

  /**
   * Currently selected applicants.
   */
  selectedApplicants = new Set<WorkspaceApplicant>()

  constructor(
    private workspacePostingStore: WorkspacePostingStore,
    private _workspacePosting: () => WorkspacePosting | null,
    private navVisible: boolean,
  ) {
    makeObservable(this, {
      showing: observable,
      columns: observable,
      searchText: observable,
      selectedApplicants: observable,
      offsetWidth: computed,
      workspacePosting: computed,
      remainingCapacity: computed,
      filteredApplicants: computed,
      show: action.bound,
      close: action.bound,
      setSearchText: action.bound,
      selectionState: computed,
      toggleApplicant: action.bound,
      toggleAllApplicants: action.bound,
      clearSelected: action.bound,
      cleanSelected: action.bound,
      inviteApplicant: action.bound,
      inviteSelected: action.bound,
      rescindInvitation: action.bound,
      rescindInvitationForSelected: action.bound,
    })
  }

  /**
   * The width used to offset the modal panel when opened.
   * @returns
   */
  get offsetWidth() {
    return this.navVisible ? 400 : 20
  }

  /**
   * The selected workspace posting.
   *  @returns
   */
  get workspacePosting() {
    return this._workspacePosting()
  }

  /**
   * The remaining capacity of the selected workspace posting.
   * @returns
   */
  get remainingCapacity() {
    if (this.workspacePosting === null) {
      return 0
    }

    const capacity = this.workspacePosting.capacity ?? 0
    const invitedOrAcceptedApplicants =
      this.workspacePosting.applicants.filter(
        (a) =>
          a.status === WorkspaceApplicantStatus.Accepted ||
          a.status === WorkspaceApplicantStatus.Invited,
      ).length ?? 0

    return capacity - invitedOrAcceptedApplicants
  }

  /**
   * The filtered set of applicants for the selected workspace posting.
   * @returns
   */
  get filteredApplicants() {
    const applicants = this.workspacePosting?.applicants ?? []

    if (!this.searchText) {
      return applicants
    }

    const text = this.searchText.toLowerCase()
    return applicants.filter(
      (a) =>
        a.displayName.toLocaleLowerCase().includes(text) ||
        a.provider.stateRegion.toLowerCase().includes(text) ||
        a.status.toLowerCase().includes(text),
    )
  }

  /**
   * Opens the applicant management table panel.
   */
  show() {
    this.showing = true
  }

  /**
   * Closes the applicant management table panel.
   */
  close() {
    this.showing = false
    this.searchText = ''
    this.clearSelected()
  }

  /**
   * Sets the search text.
   * @param value
   */
  setSearchText(value: string) {
    this.searchText = value
  }

  /**
   * Returns whether applicant is currently selected.
   * @param applicant
   * @returns
   */
  isSelected(applicant: WorkspaceApplicant): boolean {
    return this.selectedApplicants.has(applicant)
  }

  /**
   * Returns whether applicants are all selected, none selected, or in between.
   * @returns
   */
  get selectionState(): SelectionState {
    if (this.selectedApplicants.size === 0) {
      return SelectionState.NoneSelected
    }

    if (this.filteredApplicants.length === this.selectedApplicants.size) {
      return SelectionState.AllSelected
    }

    if (this.selectedApplicants.size > 0) {
      return SelectionState.SomeSelected
    }

    return SelectionState.NoneSelected
  }

  /**
   * Toggles whether an individual applicant is selected.
   * @param applicant
   */
  toggleApplicant(applicant: WorkspaceApplicant) {
    if (this.isSelected(applicant)) {
      this.selectedApplicants.delete(applicant)
    } else {
      this.selectedApplicants.add(applicant)
    }
  }

  /**
   * Toggles whether all applicants are selected.
   */
  toggleAllApplicants() {
    switch (this.selectionState) {
      case SelectionState.NoneSelected:
      case SelectionState.SomeSelected:
        this.filteredApplicants.forEach((applicant) => {
          this.selectedApplicants.add(applicant)
        })
        break
      case SelectionState.AllSelected:
        this.clearSelected()
        break
    }
  }

  /**
   * Removes all selected applicants.
   */
  clearSelected() {
    this.selectedApplicants.clear()
  }

  /**
   * Removes applicants not present in current filtered applicants.
   */
  cleanSelected() {
    for (const applicant of this.selectedApplicants) {
      if (!this.filteredApplicants.some((a) => a === applicant)) {
        this.selectedApplicants.delete(applicant)
      }
    }
  }

  /**
   * Invites an applicant.
   * @param applicant
   */
  async inviteApplicant(applicant: WorkspaceApplicant) {
    if (this.workspacePosting === null || this.remainingCapacity < 1) {
      return
    }
    const postingId = this.workspacePosting.id

    await this.workspacePostingStore
      .inviteApplicant(postingId, applicant.userPublicId)
      .then(async () => {
        await this.workspacePostingStore.fetchWorkspacePosting(postingId)
      })
  }

  /**
   * Rescinds an invitation.
   * @param applicant
   */
  async rescindInvitation(applicant: WorkspaceApplicant) {
    if (this.workspacePosting === null) {
      return
    }
    const postingId = this.workspacePosting.id

    await this.workspacePostingStore
      .rescindInvitation(postingId, applicant.userPublicId)
      .then(() => this.workspacePostingStore.fetchWorkspacePosting(postingId))
  }

  /**
   * Invites the selected applicants.
   */
  async inviteSelected() {
    if (
      this.workspacePosting === null ||
      this.selectedApplicants.size === 0 ||
      this.remainingCapacity < 1
    ) {
      return
    }
    const postingId = this.workspacePosting.id

    await Promise.all(
      Array.from(this.selectedApplicants).map((applicant) =>
        this.workspacePostingStore.inviteApplicant(
          postingId,
          applicant.userPublicId,
        ),
      ),
    ).then(async () => {
      await this.workspacePostingStore.fetchWorkspacePosting(postingId)
    })
  }

  /**
   * Rescinds the invitation for the selected applicants.
   */
  async rescindInvitationForSelected() {
    const invitedApplicants = [...this.selectedApplicants].filter(
      (a) => a.status === WorkspaceApplicantStatus.Invited,
    )
    if (this.workspacePosting === null || invitedApplicants.length === 0) {
      return
    }

    const postingId = this.workspacePosting.id

    await Promise.all(
      invitedApplicants.map((applicant) =>
        this.workspacePostingStore.rescindInvitation(
          postingId,
          applicant.userPublicId,
        ),
      ),
    ).then(async () => {
      await this.workspacePostingStore.fetchWorkspacePosting(postingId)
    })
  }
}
